// File: /opt/dev_environment/assets/workspace/app-init/src/pages/StatistikPage/StatistikPage.js

import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVocabulary } from '../../context/VocabularyContext';
import { LEITNER_COMPARTMENTS } from '../../constants/leitner';
import DataExportSection from '../../components/DataExportSection/DataExportSection';
import './StatistikPage.css';

const DEFAULT_ERROR_LIST_LIMIT = 20;

function StatistikPage() {
  const navigate = useNavigate();
  const {
    cards,
    verbCards,
    learnedWords,
    learnedVerbs,
    reviews,
    isLoaded,
    resetAll,
  } = useVocabulary();

  const [showAllErrors, setShowAllErrors] = useState(false);
  const [modeFilter, setModeFilter] = useState('alle');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const filteredCards = useMemo(() => {
    if (modeFilter === 'vokabeln') return cards;
    if (modeFilter === 'verben') return verbCards;
    return [...cards, ...verbCards];
  }, [cards, verbCards, modeFilter]);

  const filteredLearned = useMemo(() => {
    if (modeFilter === 'vokabeln') return learnedWords;
    if (modeFilter === 'verben') return learnedVerbs;
    return [...learnedWords, ...learnedVerbs];
  }, [learnedWords, learnedVerbs, modeFilter]);

  const filteredReviews = useMemo(() => {
    if (modeFilter === 'vokabeln') return reviews.filter((r) => r.cardType !== 'verb');
    if (modeFilter === 'verben') return reviews.filter((r) => r.cardType === 'verb');
    return reviews;
  }, [reviews, modeFilter]);

  const counts = useMemo(() => {
    const c = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    filteredCards.forEach((card) => {
      if (c[card.compartment] !== undefined) {
        c[card.compartment]++;
      }
    });
    return c;
  }, [filteredCards]);

  const totalActiveCards = filteredCards.length;
  const totalLearnedWords = filteredLearned.length;
  const totalReviewSessions = filteredReviews.length;
  const hasAnyCards = totalActiveCards > 0 || totalLearnedWords > 0;
  const hasReviews = totalReviewSessions > 0;

  const progressPercentage = useMemo(() => {
    const total = totalActiveCards + totalLearnedWords;
    if (total === 0) return 0;
    return Math.round((totalLearnedWords / total) * 100);
  }, [totalActiveCards, totalLearnedWords]);

  const cardsDueToday = useMemo(() => {
    const now = new Date();
    return filteredCards.filter((card) => {
      return new Date(card.nextReviewAt) <= now;
    }).length;
  }, [filteredCards]);

  const maxCompartmentCount = useMemo(() => {
    return Math.max(...Object.values(counts), 1);
  }, [counts]);

  const errorRateList = useMemo(() => {
    if (!hasReviews) return [];

    const reviewsByCard = {};
    filteredReviews.forEach((review) => {
      if (!reviewsByCard[review.cardId]) {
        reviewsByCard[review.cardId] = { correct: 0, incorrect: 0 };
      }
      if (review.correct) {
        reviewsByCard[review.cardId].correct++;
      } else {
        reviewsByCard[review.cardId].incorrect++;
      }
    });

    const allKnownCards = [
      ...cards.map((c) => ({ id: c.id, label: c.germanWord, sublabel: c.italianWord, type: 'vocab' })),
      ...verbCards.map((c) => ({ id: c.id, label: c.baseVerb, sublabel: c.targetFormDescription, type: 'verb' })),
      ...learnedWords.map((w) => ({ id: w.id, label: w.germanWord, sublabel: w.italianWord, type: 'vocab' })),
      ...learnedVerbs.map((w) => ({ id: w.id, label: w.baseVerb, sublabel: w.targetFormDescription, type: 'verb' })),
    ];

    const cardMap = new Map(allKnownCards.map((c) => [c.id, c]));

    const rateList = Object.entries(reviewsByCard)
      .filter(([, stats]) => (stats.correct + stats.incorrect) > 0)
      .map(([cardId, stats]) => {
        const card = cardMap.get(cardId);
        const total = stats.correct + stats.incorrect;
        const errorRate = Math.round((stats.incorrect / total) * 100);
        return {
          cardId,
          label: card ? card.label : '(gelöscht)',
          sublabel: card ? card.sublabel : '—',
          cardType: card ? card.type : 'unknown',
          correct: stats.correct,
          incorrect: stats.incorrect,
          total,
          errorRate,
        };
      })
      .sort((a, b) => b.errorRate - a.errorRate || b.total - a.total);

    return rateList;
  }, [filteredReviews, cards, verbCards, learnedWords, learnedVerbs, hasReviews]);

  const displayedErrorList = useMemo(() => {
    if (showAllErrors) return errorRateList;
    return errorRateList.slice(0, DEFAULT_ERROR_LIST_LIMIT);
  }, [errorRateList, showAllErrors]);

  const canExpand = errorRateList.length > DEFAULT_ERROR_LIST_LIMIT;

  const handleToggleExpand = useCallback(() => {
    setShowAllErrors((prev) => !prev);
  }, []);

  const getErrorRateClass = (rate) => {
    if (rate >= 60) return 'high';
    if (rate >= 30) return 'medium';
    return 'low';
  };

  const handleResetClick = useCallback(() => {
    setShowResetDialog(true);
  }, []);

  const handleResetConfirm = useCallback(async () => {
    setIsResetting(true);
    try {
      await resetAll();
      setShowResetDialog(false);
      navigate('/');
    } catch (error) {
      console.error('Reset failed:', error);
      setIsResetting(false);
    }
  }, [resetAll, navigate]);

  const handleResetCancel = useCallback(() => {
    setShowResetDialog(false);
  }, []);

  const ringRadius = 42;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference - (progressPercentage / 100) * ringCircumference;

  if (!isLoaded) {
    return (
      <div className="statistik-page">
        <div className="statistik-page__empty">
          <span className="statistik-page__empty-icon" role="img" aria-hidden="true">⏳</span>
          <h2 className="statistik-page__empty-title">Laden…</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="statistik-page">
      <div className="statistik-page__header">
        <h2 className="statistik-page__title">
          <span className="statistik-page__title-icon" role="img" aria-hidden="true">📊</span>
          Statistik
        </h2>
        <p className="statistik-page__subtitle">
          Dein Lernfortschritt im Überblick
        </p>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-sm)' }}>
        {['alle', 'vokabeln', 'verben'].map((mode) => (
          <button
            key={mode}
            className={`verben-filter-btn ${modeFilter === mode ? 'verben-filter-btn--active' : ''}`}
            type="button"
            onClick={() => setModeFilter(mode)}
            style={{ flex: 1, textAlign: 'center' }}
          >
            {mode === 'alle' ? 'Alle' : mode === 'vokabeln' ? 'Vokabeln' : 'Verben'}
          </button>
        ))}
      </div>

      {!hasAnyCards ? (
        <div className="statistik-page__empty">
          <span className="statistik-page__empty-icon" role="img" aria-hidden="true">📊</span>
          <h2 className="statistik-page__empty-title">Statistik</h2>
          <p className="statistik-page__empty-text">
            Noch keine Statistiken verfügbar. Füge Vokabeln hinzu und starte eine Lernsitzung!
          </p>
        </div>
      ) : (
        <>
          <div className="statistik-metrics">
            <div className="statistik-metric-card">
              <span className="statistik-metric-card__icon" role="img" aria-hidden="true">🗃️</span>
              <span className="statistik-metric-card__value">{totalActiveCards}</span>
              <span className="statistik-metric-card__label">Aktive Karten</span>
            </div>

            <div className="statistik-metric-card statistik-metric-card--highlight">
              <span className="statistik-metric-card__icon" role="img" aria-hidden="true">🎓</span>
              <span className="statistik-metric-card__value">{totalLearnedWords}</span>
              <span className="statistik-metric-card__label">Gelernt</span>
            </div>

            <div className="statistik-metric-card statistik-metric-card--accent">
              <span className="statistik-metric-card__icon" role="img" aria-hidden="true">📅</span>
              <span className="statistik-metric-card__value">{cardsDueToday}</span>
              <span className="statistik-metric-card__label">Heute fällig</span>
            </div>

            <div className="statistik-metric-card">
              <span className="statistik-metric-card__icon" role="img" aria-hidden="true">🔄</span>
              <span className="statistik-metric-card__value">{totalReviewSessions}</span>
              <span className="statistik-metric-card__label">Übungen</span>
            </div>
          </div>

          <div className="statistik-progress-section">
            <h3 className="statistik-section-title">
              <span className="statistik-section-title__icon" role="img" aria-hidden="true">📈</span>
              Gesamtfortschritt
            </h3>
            <div className="statistik-progress-ring">
              <div
                className="statistik-progress-ring__svg-container"
                role="progressbar"
                aria-valuenow={progressPercentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Gesamtfortschritt: ${progressPercentage}%`}
              >
                <svg className="statistik-progress-ring__svg" viewBox="0 0 100 100">
                  <circle className="statistik-progress-ring__track" cx="50" cy="50" r={ringRadius} />
                  <circle
                    className="statistik-progress-ring__fill"
                    cx="50"
                    cy="50"
                    r={ringRadius}
                    strokeDasharray={ringCircumference}
                    strokeDashoffset={ringOffset}
                  />
                </svg>
                <span className="statistik-progress-ring__percentage">{progressPercentage}%</span>
              </div>

              <div className="statistik-progress-ring__details">
                <div className="statistik-progress-ring__detail">
                  <span className="statistik-progress-ring__detail-dot statistik-progress-ring__detail-dot--learned" />
                  <span>
                    <span className="statistik-progress-ring__detail-value">{totalLearnedWords}</span> gelernt
                  </span>
                </div>
                <div className="statistik-progress-ring__detail">
                  <span className="statistik-progress-ring__detail-dot statistik-progress-ring__detail-dot--active" />
                  <span>
                    <span className="statistik-progress-ring__detail-value">{totalActiveCards}</span> aktiv
                  </span>
                </div>
                <div className="statistik-progress-ring__detail">
                  <span>
                    <span className="statistik-progress-ring__detail-value">{totalActiveCards + totalLearnedWords}</span> gesamt
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="statistik-distribution-section">
            <h3 className="statistik-section-title">
              <span className="statistik-section-title__icon" role="img" aria-hidden="true">📦</span>
              Fächerverteilung
            </h3>
            <div className="statistik-distribution-bars" role="list" aria-label="Kartenverteilung pro Fach">
              {Object.entries(LEITNER_COMPARTMENTS).map(([num, config]) => {
                const count = counts[num] || 0;
                const widthPercent = maxCompartmentCount > 0
                  ? Math.max((count / maxCompartmentCount) * 100, 0)
                  : 0;
                const showInsideLabel = widthPercent > 20 && count > 0;

                return (
                  <div
                    key={num}
                    className="statistik-distribution-bar"
                    role="listitem"
                    aria-label={`${config.label}: ${count} Karten`}
                  >
                    <span className="statistik-distribution-bar__label">{config.label}</span>
                    <div className="statistik-distribution-bar__track">
                      <div
                        className={`statistik-distribution-bar__fill statistik-distribution-bar__fill--${num}`}
                        style={{ width: `${widthPercent}%` }}
                      >
                        {showInsideLabel && (
                          <span className="statistik-distribution-bar__count--inside">{count}</span>
                        )}
                      </div>
                    </div>
                    {!showInsideLabel && (
                      <span className="statistik-distribution-bar__count">{count}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="statistik-errors-section">
            <div className="statistik-errors-section__header">
              <h3 className="statistik-section-title" style={{ marginBottom: 0 }}>
                <span className="statistik-section-title__icon" role="img" aria-hidden="true">⚠️</span>
                Fehlerquote pro Karte
              </h3>
            </div>

            {!hasReviews || errorRateList.length === 0 ? (
              <div className="statistik-errors-empty">
                <span className="statistik-errors-empty__icon" role="img" aria-hidden="true">📝</span>
                <p className="statistik-errors-empty__text">
                  Noch keine Übungen absolviert. Starte eine Lernsitzung, um deine Statistiken zu sehen!
                </p>
              </div>
            ) : (
              <>
                <table className="statistik-errors-table" role="table" aria-label="Fehlerquote pro Karte">
                  <thead>
                    <tr>
                      <th scope="col">Wort/Verb</th>
                      <th scope="col">Detail</th>
                      <th scope="col">Versuche</th>
                      <th scope="col">Fehler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedErrorList.map((entry) => {
                      const rateClass = getErrorRateClass(entry.errorRate);
                      return (
                        <tr key={entry.cardId}>
                          <td>
                            {entry.label}
                            {entry.cardType === 'verb' && (
                              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)', marginLeft: '4px' }}>🔤</span>
                            )}
                          </td>
                          <td style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-light)' }}>
                            {entry.sublabel}
                          </td>
                          <td>
                            <span className="statistik-attempts">
                              <span className="statistik-attempts__correct">✓{entry.correct}</span>
                              {' '}
                              <span className="statistik-attempts__incorrect">✗{entry.incorrect}</span>
                            </span>
                          </td>
                          <td>
                            <div className={`statistik-error-rate statistik-error-rate--${rateClass}`}>
                              <span>{entry.errorRate}%</span>
                              <div className="statistik-error-rate__bar">
                                <div
                                  className={`statistik-error-rate__bar-fill statistik-error-rate__bar-fill--${rateClass}`}
                                  style={{ width: `${entry.errorRate}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {canExpand && (
                  <button
                    className={`statistik-expand-btn ${showAllErrors ? 'statistik-expand-btn--expanded' : ''}`}
                    type="button"
                    onClick={handleToggleExpand}
                    aria-expanded={showAllErrors}
                  >
                    <span className="statistik-expand-btn__icon" aria-hidden="true">▼</span>
                    {showAllErrors
                      ? `Weniger anzeigen (Top ${DEFAULT_ERROR_LIST_LIMIT})`
                      : `Alle ${errorRateList.length} Karten anzeigen`
                    }
                  </button>
                )}
              </>
            )}
          </div>

          {filteredLearned.length > 0 && (
            <div className="statistik-distribution-section">
              <h3 className="statistik-section-title">
                <span className="statistik-section-title__icon" role="img" aria-hidden="true">🎓</span>
                Gelernte Wörter ({filteredLearned.length})
              </h3>
              <table className="statistik-errors-table" role="table" aria-label="Gelernte Wörter">
                <thead>
                  <tr>
                    <th scope="col">Wort/Verb</th>
                    <th scope="col">Übersetzung/Form</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLearned
                    .sort((a, b) => {
                      const aLabel = a.germanWord || a.baseVerb || '';
                      const bLabel = b.germanWord || b.baseVerb || '';
                      return aLabel.localeCompare(bLabel, 'de');
                    })
                    .map((entry) => (
                      <tr key={entry.id}>
                        <td>{entry.germanWord || entry.baseVerb}</td>
                        <td>{entry.italianWord || entry.targetFormDescription}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <DataExportSection />

      <div className="statistik-danger-zone">
        <h3 className="statistik-danger-zone__title">
          <span className="statistik-danger-zone__title-icon" role="img" aria-hidden="true">⚠️</span>
          Gefahrenzone
        </h3>
        <p className="statistik-danger-zone__description">
          Setze alle Daten zurück und beginne von vorne. Dieser Vorgang kann nicht rückgängig gemacht werden.
        </p>
        <button
          className="statistik-danger-zone__btn"
          type="button"
          onClick={handleResetClick}
          aria-label="Alle Daten zurücksetzen"
        >
          <span className="statistik-danger-zone__btn-icon" role="img" aria-hidden="true">🗑️</span>
          Alle Daten löschen
        </button>
      </div>

      {showResetDialog && (
        <div
          className="statistik-reset-overlay"
          onClick={handleResetCancel}
          role="alertdialog"
          aria-modal="true"
          aria-label="Daten zurücksetzen"
        >
          <div className="statistik-reset-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="statistik-reset-dialog__icon" role="img" aria-hidden="true">
              ⚠️
            </div>
            <h3 className="statistik-reset-dialog__title">
              Alle Daten löschen?
            </h3>
            <p className="statistik-reset-dialog__message">
              Möchtest du wirklich alle Daten löschen? Alle Vokabeln, Verbkarten, Lernfortschritt und Statistiken werden unwiderruflich gelöscht.
            </p>
            <div className="statistik-reset-dialog__actions">
              <button
                className="statistik-reset-dialog__btn statistik-reset-dialog__btn--cancel"
                type="button"
                onClick={handleResetCancel}
                disabled={isResetting}
              >
                Abbrechen
              </button>
              <button
                className="statistik-reset-dialog__btn statistik-reset-dialog__btn--confirm"
                type="button"
                onClick={handleResetConfirm}
                disabled={isResetting}
              >
                {isResetting ? 'Wird gelöscht…' : 'Alle Daten löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatistikPage;